// useEffect handles side effects in functional components. | A side effect is anything your component does outside of rendering UI. e.g Fetching data from an API, Subscribing/unsubscribing to events (like WebSocket, DOM events)
// Why useEffect is important - React re-renders many times. Every time your component re-renders, the code inside your function runs again. useEffect is here to guard this
import React, { useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';
import MobileNav from '../components/MobileNav';
import logo from '../assets/images/wordb_logo.svg';
import { FaCrown } from "react-icons/fa6";
import Footer from '../components/Footer';
import 'animate.css';
import { jwtDecode } from "jwt-decode";
import Feedback from '../components/Feedback';
import Query from '../components/Query';
import { BiHome } from "react-icons/bi";
import { Link } from 'react-router-dom';
import { API, resolveBackendPath } from '../config';
import { Helmet } from "react-helmet-async";

const Leaderboard = () => {
  const defAvatar = '/assets/images/def_avatar.jpg';

  function resolveAvatar(src) {
    return resolveBackendPath(src, defAvatar);
  }

  const [leaderboard, setLeaderboard] = useState([]);
  const [currentUser, setCurrentUser] = useState(null); // Store current user;

  const [isShaking, setIsShaking] = useState(false);
  const shakeTimer = useRef(null);

  const [changedIds, setChangedIds] = useState(new Set());   // who moved
  const prevBoard = useRef([]);                               // last snapshot

  
  const [isFeedbackOpen, setFeedbackOpen] = useState(false);
  const [isQueryOpen, setQueryOpen] = useState(false);
  const toggleFeedback = () => setFeedbackOpen(open => !open);
  const toggleQuery = () => setQueryOpen(open => !open);

  const booted = useRef(false);



  useEffect(() => {
    const socket = io(`${API}/public`, {
      withCredentials: true
    });
    socket.on('connect_error', (err) => console.warn('leaderboard socket error:', err.message));


const uname = s => String(s || '').toLowerCase();

function getScoreChangedUsernames(prevArr, nextArr) {
  const prevScore = new Map(prevArr.map(p => [uname(p.username), Number(p.latestScore) || 0]));
  const nextScore = new Map(nextArr.map(p => [uname(p.username), Number(p.latestScore) || 0]));
  const nextDisplay = new Map(nextArr.map(p => [uname(p.username), p.username]));

  const changed = [];
  for (const [key, nScore] of nextScore.entries()) {
    const pScore = prevScore.get(key);
    //if (pScore == null) continue; // ignore brand-new entries (remove to animate newcomers)
    if (nScore !== pScore) {
      changed.push(nextDisplay.get(key) ?? key);
      const delta = nScore - pScore;
      //console.log(`${nextDisplay.get(key) ?? key} scores updated (${pScore} → ${nScore}, ${delta > 0 ? '+' : ''}${delta})`);
    }
  }
  return changed;
}




    // Listen for leaderboard updates
    socket.on('leaderboardUpdate', (incoming) => {
      if (!booted.current) {
        // first time we ever see data (REST failed or just starting)
        setLeaderboard(incoming);
        prevBoard.current = incoming;
        booted.current = true;
        return;
      }

      // compute movement (rank or score)
  const changedUsers = getScoreChangedUsernames(prevBoard.current, incoming);
   //console.log('[LB] changedUsers:', changedUsers); // <-- log THIS, not state

   //setChangedIds(new Set(movedUsernames));
   //setTimeout(() => setChangedIds(new Set()), 1200); // match your CSS duration

  // 1) update the board so the UI re-renders with new scores/ranks
  setLeaderboard(incoming);
  prevBoard.current = incoming;

  // 2) after the DOM paints the new list, add the .changed class
  if (changedUsers.length) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setChangedIds(new Set(changedUsers));
        // clear so the same user can re-animate on next update
        setTimeout(() => setChangedIds(new Set()), 1300);
      });
    });
  }

      // 3) subtle board shake (do it always, or wrap with if (changedUsers.length))
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // clear any previous timer so rapid updates can retrigger
          if (shakeTimer.current) clearTimeout(shakeTimer.current);
          setIsShaking(true);
          shakeTimer.current = setTimeout(() => setIsShaking(false), 500); // match CSS duration
        });
      });
    });


    // Fetch initial leaderboard data
    const fetchLeaderboard = async () => {
      try {
        const response = await fetch(`${API}/api/game/leaderboard`);
        const data = await response.json();
        setLeaderboard(data);
        prevBoard.current = data;
        booted.current = true;
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
      }
    };

    fetchLeaderboard();

    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    const el = document.getElementById(`player-${currentUser.username}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [leaderboard, currentUser]);    // runs every time the board updates

    useEffect(() => {
      const token = localStorage.getItem('authToken');
      if (token) {
          try {
              const decoded = jwtDecode(token);
              const userId = decoded.id.toString(); // Extract the user's ID from the token
              //console.log('LB:', leaderboard);
              if (leaderboard.length > 0) {
                  const user = leaderboard.find(player => String(player._id) === userId);
                  //console.log('Current User:', user);
                  //console.log('Decoded Token ID:', decoded.id.toString());
                  setCurrentUser(user || null);
              }
          } catch (error) {
              console.error('Error decoding token:', error);
          }
      }
  }, [leaderboard]); // Run this effect whenever the leaderboard updates


  // Sort players by score (descending order)
  const sortedPlayers = [...leaderboard].sort((a, b) => b.latestScore - a.latestScore);
  //console.log('Sorted Players:', sortedPlayers);

  // Top 3 players for the top section
  const [first, second, third] = sortedPlayers;

  function isMe(name) {
    // if no one is logged in, always return false
    if (!currentUser) return false;

    // otherwise compare case-insensitively
    return (
      typeof name === 'string' &&
      name.toLowerCase() === currentUser?.username.toLowerCase()
    );
  }

    // log when the Set actually changes (state is async!)
  // useEffect(() => {
    // console.log('[LB] changedIds state now:', [...changedIds]);
  // }, [changedIds]);

    // confirm the DOM element has the class when we think it does
 // useEffect(() => {
 //   if (changedIds.size === 0) return;
 //   for (const name of changedIds) {
 //     const el = document.getElementById(`player-${name}`);
      //console.log('[LB] DOM class for', name, '→', el?.className);
  //  }
  //}, [changedIds]);

  return (
    <>
      <Helmet>
        <title>WordB Leaderboard — Top Players & Rankings</title>
        <meta
          name="description"
          content="See the top WordB players, rankings, and compete to climb the leaderboard."
        />
      </Helmet>
      <section className='leaderboard'>

        <Link to="/home" className='left-icon-link'>
          <BiHome className='icon' />
        </Link>

        <MobileNav />

        {isFeedbackOpen && <Feedback onClose={toggleFeedback} />}
        {isQueryOpen && <Query onClose={toggleQuery} />}


        <div className="logo-div">
          <img src={logo} alt="Game Logo" />
        </div>

        {/* Top 3 Players */}
        <div className={`top-section ${isShaking ? 'lb-shake' : ''}`}>
          {second && (
            <div className={
              `second top-players 
              ${changedIds.has(second.username) ? 'animate__animated animate__rubberBand' : ''}`
            }
            >
              <div className={`avatar ${isMe(second.username)  ? 'me-top' : ''}`}>
                <img 
                  src={resolveAvatar(second.avatar)}
                  onError={(e) => { e.currentTarget.src = defAvatar; }}
                  alt={second.username}
                />
              </div>
              <p className="nickname">{second.username}</p>
              <div className="rank-box">
                <p className="rank">2</p>
                <p className="score">{second.latestScore}</p>
              </div>
            </div>
          )}

          {first && (
            <div className={
              `first top-players 
              ${changedIds.has(first.username) ? 'animate__animated animate__rubberBand' : ''}`
            }
            >
              <FaCrown className='crown-icon'/>
              <div className={`avatar ${isMe(first.username)  ? 'me-top' : ''}`}>
                <img 
                  src={resolveAvatar(first.avatar)}
                  onError={(e) => { e.currentTarget.src = defAvatar; }}
                  alt={first.username}
                />
              </div>
              <p className="nickname">{first.username}</p>
              <div className="rank-box">
                <p className="rank">1</p>
                <p className="score">{first.latestScore}</p>
              </div>
            </div>
          )}

          {third && (
            <div className={
              `third top-players 
              ${changedIds.has(third.username) ? 'animate__animated animate__rubberBand' : ''}`
            }
            >
              <div className={`avatar ${isMe(third.username)  ? 'me-top' : ''}`}>
                <img 
                  src={resolveAvatar(third.avatar)}
                  onError={(e) => { e.currentTarget.src = defAvatar; }}
                  alt={third.username}
                />
              </div>
              <p className="nickname">{third.username}</p>
              <div className="rank-box">
                <p className="rank">3</p>
                <p className="score">{third.latestScore}</p>
              </div>
            </div>
          )}
        </div>

        {/* Remaining Players */}
        <div className={`bottom-section ${isShaking ? 'lb-shake' : ''}`}>
          {sortedPlayers.slice(3).map((player, index) => (
            <div 
            className={`player ${changedIds.has(player.username) ? 'changed' : ''}
              ${isMe(player.username) ? 'me' : ''}`}
            key={player.username}
            id={`player-${player.username}`}
>
              <div className="left">
                <p className="rank">{index + 4}</p>
                <div className="avatar-nickname">
                  <div className="avatar">
                    <img 
                      src={resolveAvatar(player.avatar)}
                      onError={(e) => { e.currentTarget.src = defAvatar; }}
                      alt={player.username}
                    />
                  </div>
                  <p className="nickname">{player.username}</p>
                </div>
              </div>
              <div className="right">
                <p className="score">{player.latestScore}</p>
              </div>
            </div>
          ))}
        </div>

        <Footer onOpenFeedback={toggleFeedback} onOpenQuery={toggleQuery}/>
      </section>
    </>
  );
};

export default Leaderboard;
